"use client";

import * as React from "react";
import Link from "next/link";
import { ChevronRight, Tag } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

type VersionEntry = {
  id: string;
  label: string;
  tag?: string;
  date: string;
  summary: string;
};

export function ChangelogVersionPicker({
  versions,
}: {
  versions: VersionEntry[];
}) {
  const [selected, setSelected] = React.useState(versions[0]?.id ?? "");
  const current = versions.find((v) => v.id === selected) ?? versions[0];

  return (
    <div className="not-prose space-y-6">
      <Card className="border-border/70 bg-card">
        <CardContent className="p-5 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Selecciona una versión
              </p>
              <h2 className="text-lg font-semibold tracking-tight text-foreground">
                Historial de releases
              </h2>
            </div>
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-xs outline-none transition-colors focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 sm:w-64"
            >
              {versions.map((v) => (
                <option key={v.id} value={v.id}>
                  v{v.label} · {v.date}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {current && (
        <Card className="border-border/70 bg-card">
          <CardContent className="p-5 sm:p-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1 border-primary/40 bg-primary/10 text-primary">
                <Tag className="h-3 w-3" />v{current.label}
              </Badge>
              {current.tag && <Badge variant="secondary">{current.tag}</Badge>}
              <span className="text-[11px] text-muted-foreground">
                {current.date}
              </span>
            </div>
            <h3 className="mt-3 text-xl font-semibold tracking-tight text-foreground">
              ¿Qué hay de nuevo en v{current.label}?
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">{current.summary}</p>
            <Link
              href={`/docs/changelogs/${current.id}`}
              className="mt-4 inline-flex h-9 items-center gap-1.5 rounded-md bg-[var(--primary)] px-4 text-sm font-medium text-[var(--primary-foreground)] shadow-xs transition-colors hover:opacity-90"
            >
              Ver desglose completo
              <ChevronRight className="h-4 w-4" />
            </Link>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        <Separator />
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Todas las versiones
        </p>
        <div className="space-y-2">
          {versions.map((v) => (
            <Link
              key={v.id}
              href={`/docs/changelogs/${v.id}`}
              className="block"
            >
              <Card className="border-border/70 bg-card transition-colors hover:border-primary/40 hover:bg-muted/30">
                <CardContent className="flex items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold tracking-tight text-foreground">
                        v{v.label}
                      </span>
                      {v.tag && <Badge variant="secondary">{v.tag}</Badge>}
                      <span className="text-[11px] text-muted-foreground">
                        {v.date}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-xs text-muted-foreground">
                      {v.summary}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
